'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarCheck, Clock, Target } from 'lucide-react';

interface PersonaOption {
  id: string;
  emoji: string;
  label: string;
  desc: string;
  greeting: string;
  followUp: string;
  color: string;
  colorLight: string;
  colorMid: string;
  checkinMood: string;
  timerLabel: string;
  goals: string[];
}

const personas: PersonaOption[] = [
  {
    id: 'elementary',
    emoji: '🎒',
    label: '초등학생',
    desc: '친근한 친구 톤',
    greeting: '오늘도 잘 왔어! 같이 재미있는 거 해보자 🌟',
    followUp: '어제보다 더 잘하고 있어! 이 기세로 가보자~',
    color: '#f97316',
    colorLight: '#fff7ed',
    colorMid: '#fdba74',
    checkinMood: '신나는 하루!',
    timerLabel: '공부 시간',
    goals: ['일기 쓰기', '수학 문제 풀기', '책 읽기'],
  },
  {
    id: 'secondary',
    emoji: '📚',
    label: '중·고등학생',
    desc: '함께하는 멘토 톤',
    greeting: '오늘 뭐 할 거야? 같이 정리해보자!',
    followUp: '집중력이 올라가고 있어. 이 흐름 이어가자!',
    color: '#7c3aed',
    colorLight: '#f5f3ff',
    colorMid: '#c4b5fd',
    checkinMood: '집중 모드',
    timerLabel: '집중 타이머',
    goals: ['영어 단어 외우기', '수학 문제집', '과학 정리'],
  },
  {
    id: 'university',
    emoji: '🎓',
    label: '대학생',
    desc: '전략적 코치 톤',
    greeting: '이번 주 계획, 어디까지 왔어요?',
    followUp: '시간 관리가 눈에 띄게 좋아지고 있어요. 이 페이스 유지해보세요.',
    color: '#4f46e5',
    colorLight: '#eef2ff',
    colorMid: '#a5b4fc',
    checkinMood: '생산적인 하루',
    timerLabel: '딥워크',
    goals: ['논문 읽기', '프로젝트 진행', '포트폴리오'],
  },
  {
    id: 'professional',
    emoji: '💼',
    label: '사회인',
    desc: '실용적 코치 톤',
    greeting: '오늘의 목표와 컨디션, 같이 점검해볼까요?',
    followUp: '이번 주 달성률이 올라가고 있어요. 이 흐름을 이어가세요.',
    color: '#2563eb',
    colorLight: '#eff6ff',
    colorMid: '#93c5fd',
    checkinMood: '집중하는 하루',
    timerLabel: '집중 시간',
    goals: ['자격증 공부', '운동 30분', '독서 1챕터'],
  },
];

export default function PersonaPreview() {
  const [selected, setSelected] = useState(1);
  const active = personas[selected];

  return (
    <section className="py-24 sm:py-32">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* PC: 2-column layout | Mobile: stacked */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-16">

          {/* Left column: heading + selector */}
          <div className="lg:flex-1 lg:sticky lg:top-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
              className="mb-10 lg:mb-12"
            >
              {/* Coach speech bubble */}
              <div className="inline-flex items-start gap-3 mb-8">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #4338ca)' }}
                >
                  AI
                </div>
                <div
                  className="rounded-2xl rounded-tl-sm px-5 py-3 text-left text-sm leading-relaxed text-gray-700"
                  style={{ backgroundColor: '#f3f0ff', maxWidth: '340px' }}
                >
                  사람마다 잘 맞는 코칭 스타일이 달라요.
                  <br />
                  아래에서 눌러보면 톤이 어떻게 바뀌는지 느낄 수 있어요.
                </div>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
                나에게 딱 맞는 코치
              </h2>
              <p className="text-gray-500 text-base sm:text-lg">
                나이와 상황에 맞춰 코칭 톤이 달라져요.
              </p>
            </motion.div>

            {/* Persona selector — grid 4-col, equal width, no wrap */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-10 lg:mb-0 max-w-md" role="tablist" aria-label="페르소나 선택">
              {personas.map((p, idx) => {
                const isActive = selected === idx;
                return (
                  <button
                    key={p.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setSelected(idx)}
                    className="flex flex-col items-center gap-1.5 py-3 sm:py-4 rounded-2xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    style={{
                      backgroundColor: isActive ? p.colorLight : '#f9fafb',
                      border: isActive ? `2px solid ${p.color}` : '2px solid transparent',
                      boxShadow: isActive ? `0 4px 14px ${p.color}20` : 'none',
                    }}
                  >
                    <span className="text-xl sm:text-2xl" aria-hidden="true">{p.emoji}</span>
                    <span
                      className="text-[10px] sm:text-xs font-bold whitespace-nowrap"
                      style={{ color: isActive ? p.color : '#9ca3af' }}
                    >
                      {p.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right column: preview card */}
          <div className="lg:flex-1 lg:max-w-lg">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
                className="rounded-3xl shadow-xl overflow-hidden bg-white border border-gray-100"
              >
                {/* Header */}
                <div
                  className="px-6 py-5 flex items-center gap-3"
                  style={{ backgroundColor: active.colorLight }}
                >
                  <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center text-2xl"
                    style={{ backgroundColor: `${active.color}15` }}
                  >
                    {active.emoji}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">{active.label} 모드</p>
                    <p className="text-xs text-gray-500">{active.desc}</p>
                  </div>
                  <div
                    className="px-2.5 py-1 rounded-full text-[10px] font-bold"
                    style={{ backgroundColor: `${active.color}15`, color: active.color }}
                  >
                    활성
                  </div>
                </div>

                {/* Chat interface area */}
                <div className="px-6 pt-5 pb-3">
                  {/* AI greeting */}
                  <div className="flex gap-2.5 mb-3">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold text-white"
                      style={{ backgroundColor: active.color }}
                    >
                      AI
                    </div>
                    <div>
                      <div
                        className="rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed"
                        style={{ backgroundColor: active.colorLight, color: '#374151' }}
                      >
                        {active.greeting}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1 ml-1">방금 전</p>
                    </div>
                  </div>

                  {/* AI follow-up */}
                  <div className="flex gap-2.5 mb-3">
                    <div className="w-7 shrink-0" />
                    <div
                      className="rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed"
                      style={{ backgroundColor: active.colorLight, color: '#374151' }}
                    >
                      {active.followUp}
                    </div>
                  </div>

                  {/* User reply */}
                  <div className="flex justify-end mb-2">
                    <div
                      className="rounded-2xl rounded-tr-sm px-4 py-3 text-sm text-white"
                      style={{ backgroundColor: active.color }}
                    >
                      고마워요! 오늘도 열심히 해볼게요
                    </div>
                  </div>

                  {/* Typing indicator */}
                  <div className="flex gap-2.5">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold text-white"
                      style={{ backgroundColor: active.color }}
                    >
                      AI
                    </div>
                    <div
                      className="rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1"
                      style={{ backgroundColor: active.colorLight }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: active.color, animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: active.color, animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: active.color, animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>

                {/* Feature previews */}
                <div className="px-6 py-4 border-t border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">미리보기</p>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Checkin card */}
                    <div
                      className="rounded-xl p-3"
                      style={{ backgroundColor: active.colorLight }}
                    >
                      <div className="flex items-center gap-1.5 mb-2">
                        <CalendarCheck className="w-3.5 h-3.5" style={{ color: active.color }} />
                        <span className="text-[10px] font-bold text-gray-700">체크인</span>
                      </div>
                      <p className="text-[10px] font-medium" style={{ color: active.color }}>{active.checkinMood}</p>
                      <div className="mt-1.5 flex gap-0.5">
                        {[3, 4, 5, 3, 4].map((v, i) => (
                          <div
                            key={i}
                            className="flex-1 rounded-sm"
                            style={{
                              height: `${v * 4}px`,
                              backgroundColor: i === 2 ? active.color : active.colorMid,
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Timer card */}
                    <div
                      className="rounded-xl p-3"
                      style={{ backgroundColor: active.colorLight }}
                    >
                      <div className="flex items-center gap-1.5 mb-2">
                        <Clock className="w-3.5 h-3.5" style={{ color: active.color }} />
                        <span className="text-[10px] font-bold text-gray-700">{active.timerLabel}</span>
                      </div>
                      <div className="flex items-center justify-center">
                        <div
                          className="w-10 h-10 rounded-full border-2 flex items-center justify-center"
                          style={{ borderColor: active.colorMid, borderTopColor: active.color }}
                        >
                          <span className="text-[9px] font-black" style={{ color: active.color }}>25:00</span>
                        </div>
                      </div>
                    </div>

                    {/* Goals card */}
                    <div
                      className="rounded-xl p-3"
                      style={{ backgroundColor: active.colorLight }}
                    >
                      <div className="flex items-center gap-1.5 mb-2">
                        <Target className="w-3.5 h-3.5" style={{ color: active.color }} />
                        <span className="text-[10px] font-bold text-gray-700">목표</span>
                      </div>
                      <div className="space-y-1">
                        {active.goals.map((g, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <div
                              className="w-2.5 h-2.5 rounded-full border-[1.5px] flex items-center justify-center shrink-0"
                              style={{
                                borderColor: i === 0 ? active.color : active.colorMid,
                                backgroundColor: i === 0 ? active.color : 'transparent',
                              }}
                            >
                              {i === 0 && (
                                <svg className="w-1.5 h-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span
                              className="text-[9px] truncate"
                              style={{
                                color: i === 0 ? '#9ca3af' : '#374151',
                                textDecoration: i === 0 ? 'line-through' : 'none',
                              }}
                            >
                              {g}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
